import { useEffect, useState } from 'react'
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'

function Label({ children }: { children: ReactNode }) {
  return <label className="block text-[11px] font-medium text-text-muted mb-1">{children}</label>
}

export function TextField({
  label,
  ...rest
}: { label: string } & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="text"
        className="w-full rounded bg-ink-bg/60 border border-ink-border px-2.5 py-1.5 text-sm text-text placeholder:text-text-faint focus:outline-none focus:border-accent-blue/60"
        {...rest}
      />
    </div>
  )
}

export function TextAreaField({
  label,
  ...rest
}: { label: string } & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <div>
      <Label>{label}</Label>
      <textarea
        className="w-full rounded bg-ink-bg/60 border border-ink-border px-2.5 py-1.5 text-sm text-text placeholder:text-text-faint focus:outline-none focus:border-accent-blue/60 resize-y"
        {...rest}
      />
    </div>
  )
}

export function SelectField({
  label,
  children,
  ...rest
}: { label: string; children: ReactNode } & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div>
      <Label>{label}</Label>
      <select
        className="w-full rounded bg-ink-bg/60 border border-ink-border px-2.5 py-1.5 text-sm text-text focus:outline-none focus:border-accent-blue/60"
        {...rest}
      >
        {children}
      </select>
    </div>
  )
}

interface SliderFieldProps {
  label: string
  value: number
  min: number
  max: number
  step?: number
  unit?: string
  onChange: (v: number) => void
  /**
   * UI MK2 第2段 2b: unlike TextField/SelectField/TextAreaField (which spread
   * `...rest` onto the underlying element and so already accept
   * `data-testid`), SliderField has a fixed prop list — this is the minimal
   * addition needed so a specific slider (e.g. FreeText の x/y) can be
   * targeted in e2e without a full rest-prop spread onto two inputs at once.
   */
  'data-testid'?: string
}

/**
 * m4 (REVIEW_v5.md, 2026-09-05): a raw JS float (e.g. `20.61270092657358`,
 * from a computed timeline position) must never reach the screen verbatim.
 * Seconds display to 1 decimal place; anything on an integer step (px, °,
 * dB — any "whole units" control) rounds to a plain integer; anything else
 * on a fractional step (e.g. `cropX`'s 0.01, a scale multiplier's 0.1)
 * displays to that step's own decimal precision — never more digits than
 * the control can actually produce by dragging/typing.
 */
function formatSliderDisplay(value: number, unit: string, step: number): string {
  if (unit === 's') return value.toFixed(1)
  if (!Number.isFinite(step) || Number.isInteger(step)) return String(Math.round(value))
  const decimals = step.toString().split('.')[1]?.length ?? 1
  return value.toFixed(decimals)
}

/**
 * m4 (REVIEW_v6.md, 2026-09-05): the number input's OWN `value` must show
 * the same rounded text as the span next to it — a raw JS float
 * (`22.533333333333335`) reaching that box is exactly the bug this fix
 * closes (REVIEW_v6.md DOM audit: `input.value`, not just the span's
 * `innerText`, is what a real audit has to check). Saved state stays the
 * FULL-PRECISION raw value (`onChange` always receives `Number(e.target.value)`
 * verbatim, never rounded) — only the box's displayed text is rounded.
 * Local state holds that displayed text so an in-progress keystroke (e.g.
 * typing "12.35") is never clobbered by a re-render rounding it mid-edit;
 * it re-syncs from the rounded value whenever `value` changes from OUTSIDE
 * this input (slider drag, reload, auto-edit) — never while this box itself
 * has focus.
 */
function useRoundedNumberInputText(display: string): {
  text: string
  onFocus: () => void
  onBlur: () => void
  setText: (t: string) => void
} {
  const [text, setText] = useState(display)
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!editing) setText(display)
  }, [display, editing])
  return { text, setText, onFocus: () => setEditing(true), onBlur: () => setEditing(false) }
}

/** Slider + a plain-number input in sync, per the "スライダー＋直接入力の両方" requirement. */
export function SliderField({
  label,
  value,
  min,
  max,
  step = 1,
  unit = '',
  onChange,
  'data-testid': dataTestId,
}: SliderFieldProps) {
  const display = formatSliderDisplay(value, unit, step)
  const numberInput = useRoundedNumberInputText(display)
  return (
    <div data-testid={dataTestId}>
      <div className="flex items-center justify-between mb-1">
        <Label>{label}</Label>
        <span className="text-[11px] text-text-muted tabular-nums">
          {display}
          {unit}
        </span>
      </div>
      <div className="flex items-center gap-2">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="flex-1 accent-accent-blue h-1"
        />
        <input
          type="number"
          min={min}
          max={max}
          step={step}
          value={numberInput.text}
          onFocus={numberInput.onFocus}
          onBlur={numberInput.onBlur}
          onChange={(e) => {
            numberInput.setText(e.target.value)
            const n = Number(e.target.value)
            if (!Number.isNaN(n)) onChange(n)
          }}
          className="w-16 rounded bg-ink-bg/60 border border-ink-border px-1.5 py-1 text-xs text-text tabular-nums focus:outline-none focus:border-accent-blue/60"
        />
      </div>
    </div>
  )
}

export function CheckboxField({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-text cursor-pointer select-none">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="accent-accent-blue w-4 h-4"
      />
      {label}
    </label>
  )
}
